import type { FC } from "react";
import { useRef, useState } from "react";

import type { AccountDropdownSettings } from "@ui/eva-components/Header/Header";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { ProfileDefaultIcon } from "@jti/brand-icons/profile-default";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { Loader } from "@jti/core-components/components/Loader";
import { useDirection } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";
import type { IUser } from "@jti/rest-api-types";

import { ActionButton } from "../../../Tools/ActionButton/ActionButton";
import { UserDialog } from "../../UserDialog";
import { getFullUserName } from "../../utils/getFullUserName";

import toolsStyles from "../../../Tools/Tools.module.scss";
import styles from "./UserIndicationButton.module.scss";

export type UserIndicationButtonProps = {
  accountDropdownSettings?: AccountDropdownSettings;
  accountLink: string;
  accountLabel?: string;
  accountLoadingLabel?: string;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  isAuthPage?: boolean;
  onLogOut: () => void;
};

export type UserIndicationAdditionalProps = { isUserLoggedIn: boolean; user: IUser | null; isUserPending: boolean };

export const UserIndicationButton: FC<UserIndicationButtonProps & UserIndicationAdditionalProps> = ({
  accountDropdownSettings,
  accountLink,
  accountLabel,
  accountLoadingLabel,
  isUserLoggedIn,
  onLogOut,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  isUserPending,
  user,
  isAuthPage = false,
}) => {
  const messageBus = useMessageBus();
  const { isSingleNameField, isReversedNameOrder } = useConfig();

  const { __ } = useTranslations();

  const userName = getFullUserName(__, user, isSingleNameField, isReversedNameOrder);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  useOutsideClick(dialogRef, () => {
    setIsUserDialogOpen(false);
  });

  const direction = useDirection();
  const isRtl = direction === "rtl";

  const publishAuthNavigation = () =>
    messageBus.publish("user.auth.root.navigation", {
      payload: null,
    });

  if (isUserPending) {
    return (
      <div className={toolsStyles.item} aria-busy="true">
        <Loader aria-label={accountLoadingLabel ?? accountLabel} />
      </div>
    );
  }

  if (!isUserLoggedIn) {
    return (
      <LinkBehaviour
        onClick={publishAuthNavigation}
        aria-label={accountLabel}
        title={accountLabel}
        href={accountLink}
        className={classNames(toolsStyles.item, {
          [toolsStyles.isActive]: isAuthPage,
        })}
        data-testid="headerLoginButton"
      >
        <ProfileDefaultIcon className={toolsStyles.icon} />
      </LinkBehaviour>
    );
  }

  return (
    <div
      ref={dialogRef}
      className={classNames({
        [styles.userButtonWrapper]: isEnabledUserIndication && isEnabledUserNameForIndication,
        [styles.userButtonWithNameRtl]: isEnabledUserIndication && isEnabledUserNameForIndication && isRtl,
      })}
    >
      <ActionButton
        onClick={() => setIsUserDialogOpen((prev) => !prev)}
        className={styles.userButtonContainer}
        aria-expanded={isUserDialogOpen}
        aria-haspopup="true"
        aria-label={userName}
      >
        <div className={classNames({ [styles.userButton]: isEnabledUserIndication })}>
          <span
            aria-hidden="true"
            className={classNames({
              [toolsStyles.item]: !isEnabledUserIndication,
              [toolsStyles.itemOpen]: !isEnabledUserIndication && isUserDialogOpen,
              [styles.userButtonLogged]: isEnabledUserIndication,
              [styles.userButtonOpen]: isEnabledUserIndication && (isUserDialogOpen || isAuthPage),
              [styles.userButtonWithName]: isEnabledUserIndication && isEnabledUserNameForIndication,
            })}
          >
            {isEnabledUserIndication ? (
              userName?.[0]?.toUpperCase()
            ) : (
              <ProfileDefaultIcon className={toolsStyles.icon} />
            )}
          </span>
        </div>
        <span
          aria-hidden="true"
          className={classNames(styles.userButtonNameGeneral, {
            [styles.userButtonName]: isEnabledUserIndication && isEnabledUserNameForIndication,
          })}
        >
          {userName}
        </span>
      </ActionButton>
      {isUserDialogOpen && (
        <UserDialog
          setIsUserDialogOpen={setIsUserDialogOpen}
          accountDropdownSettings={accountDropdownSettings}
          userName={userName}
          onLogOut={onLogOut}
          isEnabledUserIndicationWithName={!!isEnabledUserIndication && !!isEnabledUserNameForIndication}
        />
      )}
    </div>
  );
};
